import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as R from 'ramda';
import CarouselModal from './CarouselModal';
import getColumns from '../getColumns';
import { defaultTotals } from '../initialStates';
import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import PostsCampaignAssignmentMenu from './PostsCampaignAssignmentMenu';
import AssignCampaignModal from './AssignCampaignModal';
import {
    assignPostsToCampaign,
    assignPostsToCampaignsFromQuery,
} from '../api/postCampaigns';

import { removeCampaignFromPost } from '../utils';
import * as Sentry from '@sentry/browser';
import { InfoCircleTwoTone } from '@ant-design/icons';

import {
    Button as AntButton,
    Flex,
    theme,
    Typography,
    Skeleton,
    Row,
    Col,
} from '@app/ui';

import '../../components/table/table.scss';
import ResizableTable from '@app/components/common/Table';
import Empty from '@app/components/common/Empty';
import { Notification } from '@app/components/notifications/Notifications';
import StickySocialPostsTableLayout from '@app/components/social-posts/StickySocialPostsTableLayout';

type PostCampaign = { id: number; name: string };
type AuthenticatedTablePost = {
    id: number;
    postIds?: number[] | null;
    campaigns?: PostCampaign[] | null;
    selected?: boolean;
    shouldDisplaySelectionCheckbox?: boolean;
    togglePostSelection?: () => void;
    removeCampaign?: (campaignId: number) => void;
    isSelectPostCheckboxDisabled?: boolean;
};

type AuthenticatedPostsTableProps = {
    client: any;
    postsData: {
        posts?: AuthenticatedTablePost[] | null;
        totals: any;
        postCount: number;
    };
    isDataLoading: boolean;
    // eslint-disable-next-line no-unused-vars
    sortTableByColumn: (sortByColumn: any) => void;
    sortedByColumn: any;
    downloadCsv: () => void;
    queryParams: any;
    updateCampaignOptionsInFilters: () => void;
    sendNotification: (
        // eslint-disable-next-line no-unused-vars
        message: string,
        // eslint-disable-next-line no-unused-vars
        config: Notification['options'],
    ) => void;
};

const AuthenticatedPostsTable = ({
    client,
    postsData: { posts = [], totals = defaultTotals, postCount = 0 },
    sortedByColumn,
    isDataLoading = true,
    sortTableByColumn,
    downloadCsv,
    queryParams,
    updateCampaignOptionsInFilters,
    sendNotification,
}: AuthenticatedPostsTableProps) => {
    const { t } = useTranslation(['ownedSocial', 'common']);
    const [shouldDisplaySelectionCheckbox, setShouldDisplaySelectionCheckbox] =
        useState(false);
    const [tablePosts, setTablePosts] = useState<AuthenticatedTablePost[]>([]);
    const [isGeneratingCSV, setIsGeneratingCSV] = useState(false);
    const [selectedPostsCount, setSelectedPostsCount] = useState(0);
    const [postShowingMedia, setPostShowingMedia] = useState(null);
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [
        shouldDisplayAssignCampaignModal,
        setShouldDisplayAssignCampaignModal,
    ] = useState(false);

    const [
        shouldSelectAllPostsMatchingFilters,
        setShouldSelectAllPostsMatchingFilters,
    ] = useState(false);

    // Track if user explicitly toggled Select all/Unselect all
    const [isSelectAllToggled, setIsSelectAllToggled] = useState(false);
    const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());

    const hasAtLeastOneSelection = !!tablePosts.find(p => p.selected);
    const openMediaModal = useCallback(
        postId => {
            const post = tablePosts.find(p => p.id === postId);
            setPostShowingMedia(post);
            setIsMediaModalOpen(true);
        },
        [tablePosts],
    );

    const _togglePostSelection = pId => () => {
        setTablePosts(prev => {
            const newTablePosts = R.clone(prev).reduce((acc, curr) => {
                if (curr.id === pId) {
                    const nextSelected = !curr.selected;
                    curr.selected = nextSelected;
                    if (isSelectAllToggled) {
                        setExcludedIds(prevExcluded => {
                            const updated = new Set<number>(prevExcluded);
                            if (nextSelected) {
                                updated.delete(pId);
                            } else {
                                updated.add(pId);
                            }
                            return updated;
                        });
                    }
                }
                acc.push(curr);
                return acc;
            }, []);

            const newSelectedPostsCount = newTablePosts.filter(
                p => p.selected,
            ).length;
            setSelectedPostsCount(newSelectedPostsCount);
            return newTablePosts;
        });
    };

    const _removeCampaign = (pId: number) => (campaignId: number) => {
        setTablePosts(prev => removeCampaignFromPost(prev, pId, campaignId));
    };

    useEffect(() => {
        const newTablePosts = posts.map(p => ({
            ...p,
            shouldDisplaySelectionCheckbox: shouldDisplaySelectionCheckbox,
            togglePostSelection: _togglePostSelection(p.id),
            removeCampaign: _removeCampaign(p.id),
        }));

        setTablePosts(prev => {
            // filters were updated (in this case we dont wanna persis selections)
            if (posts.length <= prev.length) {
                return newTablePosts;
            }

            const prevMap = prev.reduce((acc, curr) => {
                acc[curr.id] = curr;
                return acc;
            }, {});

            return newTablePosts.map(p => {
                if (prevMap[p.id]?.selected) {
                    p.selected = true;
                }
                return p;
            });
        });
    }, [posts]);

    useEffect(() => {
        // this returns the tablePosts with selection flags updated based on dependencies
        const getUpdatedPostsList = previousPosts =>
            previousPosts.map(post => {
                post.shouldDisplaySelectionCheckbox =
                    shouldDisplaySelectionCheckbox;
                // In select-all mode, selected = true unless excluded
                if (shouldSelectAllPostsMatchingFilters) {
                    post.selected = !excludedIds.has(post.id);
                }

                // Keep checkboxes enabled to allow exclusions while in select-all mode
                post.isSelectPostCheckboxDisabled = false;
                return post;
            });

        setTablePosts(prev => getUpdatedPostsList(prev));
    }, [
        shouldDisplaySelectionCheckbox,
        shouldSelectAllPostsMatchingFilters,
        excludedIds,
    ]);

    // sets post selection checkbox as given value
    const setAllPostsSelected = (selected: boolean) => {
        setTablePosts(prev => {
            return prev.map(p => {
                p.selected = selected;
                return p;
            });
        });
    };

    const handleColumnSorting = sorted => {
        if (!sorted.length) return;
        sortTableByColumn(sorted[0].id);
    };

    const handleDisplaySelectionCheckboxChange = (
        shouldEnablePostSelection: boolean,
    ) => {
        if (!shouldEnablePostSelection) {
            setAllPostsSelected(false);
            // Reset select-all mode and exclusions when turning off selection UI
            setIsSelectAllToggled(false);
            setExcludedIds(new Set());
        }
        setShouldDisplaySelectionCheckbox(shouldEnablePostSelection);
    };

    const handleCsvDownloadRequest = async () => {
        setIsGeneratingCSV(true);
        await downloadCsv();
        setIsGeneratingCSV(false);
    };

    const handlePostsMatchingFiltersSelection =
        (shouldSelect: boolean) => () => {
            setShouldSelectAllPostsMatchingFilters(shouldSelect);
            setAllPostsSelected(shouldSelect);
            // Toggle select-all explicit state and reset exclusions accordingly
            if (shouldSelect) {
                setIsSelectAllToggled(true);
                setExcludedIds(new Set());
            } else {
                setIsSelectAllToggled(false);
                setExcludedIds(new Set());
            }
        };

    const getCarouselModal = () => {
        if (
            !postShowingMedia?.carouselMediaUrls &&
            !postShowingMedia?.cdnMediaUrl
        )
            return null;

        return (
            <CarouselModal
                onClose={() => setIsMediaModalOpen(false)}
                media={postShowingMedia}
            />
        );
    };

    const _handlePostAssignment = async (campaignId, campaignName) => {
        try {
            if (!shouldSelectAllPostsMatchingFilters) {
                const postIdsToSave = tablePosts
                    .filter(p => p.selected)
                    .flatMap(p => p.postIds);
                await assignPostsToCampaign(
                    client.id,
                    campaignId,
                    postIdsToSave,
                );

                const addCampaignToSpecificPosts = postsList =>
                    postsList.map(p => {
                        if (postIdsToSave.includes(p.id)) {
                            const hasCampaign = (p.campaigns ?? []).find(
                                c => c.id === campaignId,
                            );

                            // if already has the campaign, we skip to avoid duplicates
                            if (hasCampaign) {
                                return p;
                            }

                            p.campaigns = [
                                ...(p.campaigns ?? []),
                                { id: campaignId, name: campaignName },
                            ];
                        }

                        return p;
                    });

                // adds campaign to selected posts
                setTablePosts(prev => addCampaignToSpecificPosts(prev));
            }

            if (shouldSelectAllPostsMatchingFilters) {
                const excluded = Array.from(excludedIds.values());
                await assignPostsToCampaignsFromQuery(
                    client.id,
                    queryParams,
                    campaignId,
                    excluded,
                );

                const addCampaignToAllTablePosts = postsList =>
                    postsList.map(p => {
                        const hasCampaign = (p.campaigns ?? []).find(
                            c => c.id === campaignId,
                        );

                        // if already has the campaign, we skip to avoid duplicates
                        if (hasCampaign) {
                            return p;
                        }

                        p.campaigns = [
                            ...(p.campaigns ?? []),
                            {
                                id: campaignId,
                                name: campaignName,
                            },
                        ];
                        return p;
                    });

                // adds campaign to all posts in table
                setTablePosts(prev => addCampaignToAllTablePosts(prev));
            }

            sendNotification(
                t('socialPostMetrics.postsAssignedSuccess', { campaignName }),
                {
                    context: 'success',
                },
            );
        } catch (e) {
            sendNotification(t('socialPostMetrics.postsAssignmentFailed'), {
                context: 'danger',
            });
            Sentry.captureException(e);
        }

        updateCampaignOptionsInFilters();
        setAllPostsSelected(false);
        setShouldDisplaySelectionCheckbox(false);
        setShouldSelectAllPostsMatchingFilters(false);
    };

    const {
        token: { colorPrimary },
    } = theme.useToken();

    const isCampaignAssignAvailable =
        shouldDisplaySelectionCheckbox &&
        (hasAtLeastOneSelection || shouldSelectAllPostsMatchingFilters);

    const legacyColumns = useMemo(() => {
        const tableConfig = { sortedByColumn };
        return getColumns(
            totals,
            tableConfig,
            openMediaModal,
            client.currency?.code,
            true,
            t,
        );
    }, [totals, sortedByColumn, openMediaModal, client.currency?.code, t]);

    const antColumns = useMemo(() => {
        return legacyColumns.map(col => {
            const {
                Header: title,
                accessor,
                Cell,
                width,
                format: formatter,
            } = col;
            const dataIndex = accessor;
            const resolvedWidth = width ?? 150;
            const isCaption = accessor === 'content';
            return {
                title,
                dataIndex,
                key: dataIndex,
                width: resolvedWidth,
                ...(isCaption
                    ? {
                          maxWidth: resolvedWidth,
                          ellipsis: { showTitle: true },
                      }
                    : { ellipsis: true }),
                sorter: (a, b) => {
                    const aValue = a[accessor];
                    const bValue = b[accessor];

                    if (aValue === undefined || bValue === undefined) {
                        return 0;
                    }
                    if (typeof aValue === 'string') {
                        return aValue.localeCompare(bValue);
                    }
                    return aValue - bValue;
                },
                render: (value: any, record: any) => {
                    if (typeof Cell === 'function') {
                        return Cell({
                            value,

                            original: record,
                        });
                    }
                    if (typeof formatter === 'function') {
                        return formatter(value);
                    }
                    return value;
                },
            };
        });
    }, [legacyColumns]);

    const antdTableProps = useMemo(() => {
        const scrollX = antColumns.reduce(
            (sum, col) =>
                sum + (typeof col.width === 'number' ? col.width : 150),
            0,
        );

        const rowSelection = shouldDisplaySelectionCheckbox
            ? {
                  selectedRowKeys: isSelectAllToggled
                      ? tablePosts
                            .filter(p => !excludedIds.has(p.id))
                            .map(p => p.id)
                      : tablePosts.filter(p => p.selected).map(p => p.id),
                  onChange: (keys: React.Key[]) =>
                      setTablePosts(prev => {
                          const keySet = new Set<number>(
                              (keys as number[]) ?? [],
                          );
                          const newTablePosts = prev.map(p => ({
                              ...p,
                              selected: keySet.has(p.id),
                          }));

                          if (isSelectAllToggled) {
                              // In select-all mode, exclusions = not selected keys
                              setExcludedIds(() => {
                                  const updated = new Set<number>();
                                  newTablePosts.forEach(p => {
                                      if (!keySet.has(p.id)) {
                                          updated.add(p.id);
                                      }
                                  });
                                  return updated;
                              });
                          }

                          const newSelectedPostsCount = newTablePosts.filter(
                              p => p.selected,
                          ).length;
                          setSelectedPostsCount(newSelectedPostsCount);
                          return newTablePosts;
                      }),
              }
            : undefined;
        return {
            rowKey: 'id',
            className: 'posts-table',
            columns: antColumns,
            dataSource: tablePosts,
            scroll: { x: scrollX },
            sticky: true,
            onChange: handleColumnSorting,
            rowSelection,
            locale: {
                emptyText: isDataLoading ? (
                    <Skeleton active paragraph={{ rows: 10 }} />
                ) : (
                    <Empty />
                ),
            },
            rowClassName: (_, index) =>
                index % 2 === 0 ? 'table-row-light' : 'table-row-dark',
            bordered: true,
            size: 'small' as const,
            showSorterTooltip: false,
        };
    }, [
        antColumns,
        tablePosts,
        isDataLoading,
        shouldDisplaySelectionCheckbox,
        isSelectAllToggled,
        excludedIds,
    ]);

    return (
        <>
            <Flex
                justify="space-between"
                style={{
                    margin: '12px 0',
                }}
            >
                {!shouldDisplaySelectionCheckbox && (
                    <Flex
                        justify="center"
                        align="center"
                        style={{
                            minWidth: 300,
                        }}
                    >
                        <InfoCircleTwoTone
                            twoToneColor={colorPrimary}
                            style={{
                                fontSize: 20,
                                marginRight: 12,
                            }}
                        />{' '}
                        <Typography.Text>
                            {t('socialPostMetrics.postsDelayMessage')}
                        </Typography.Text>
                    </Flex>
                )}
                <Flex
                    gap="small"
                    wrap
                    justify="flex-end"
                    align="flex-end"
                    style={{
                        width: '100%',
                        padding: '12px 0',
                    }}
                >
                    <PostsCampaignAssignmentMenu
                        postCount={postCount}
                        selectedPostsCount={selectedPostsCount}
                        isPostSelectionEnabled={shouldDisplaySelectionCheckbox}
                        onDisplaySelectionCheckboxChange={
                            handleDisplaySelectionCheckboxChange
                        }
                        shouldDisableButtons={isDataLoading || isGeneratingCSV}
                        setPostsMatchingFiltersSelected={
                            handlePostsMatchingFiltersSelection
                        }
                        isPostsMatchingFiltersSelected={
                            shouldSelectAllPostsMatchingFilters
                        }
                        excludedIdsCount={excludedIds.size}
                        onDisplayCampaignAssigmentModal={() =>
                            setShouldDisplayAssignCampaignModal(true)
                        }
                        isCampaignAssignAvailable={isCampaignAssignAvailable}
                    />
                    <AntButton
                        color="primary"
                        variant="outlined"
                        onClick={handleCsvDownloadRequest}
                        data-test="download-csv-button"
                        disabled={isDataLoading || isGeneratingCSV}
                    >
                        <i className="fa-regular fa-download -m-r-1" />
                        {t('socialPostMetrics.exportToCsv')}
                    </AntButton>
                </Flex>
            </Flex>

            <Row gutter={16}>
                <Col md={24}>
                    <ErrorBoundary FallbackComponent={CrashedComponent}>
                        <StickySocialPostsTableLayout
                            data-test="authenticated-posts-table"
                            wrapperClassName="authenticated-posts-table"
                        >
                            <ResizableTable
                                {...antdTableProps}
                                pagination={false}
                            />
                        </StickySocialPostsTableLayout>
                    </ErrorBoundary>
                </Col>
            </Row>

            <AssignCampaignModal
                onClose={() => {
                    setShouldDisplayAssignCampaignModal(false);
                }}
                clientId={client.id}
                handlePostAssignment={_handlePostAssignment}
                visible={shouldDisplayAssignCampaignModal}
            />

            {isMediaModalOpen && getCarouselModal()}
        </>
    );
};

export default AuthenticatedPostsTable;
